import queryString from 'query-string';
import { useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { parseNumberOrDefault } from '../parseNumberOrDefault';

import { generateQueryString } from './generateQueryString';
import { parseNameParam } from './parseNameParam';
import { QueryParams } from './queryParamDeclaration';

/**
 * A custom hook that reads and updates URL query params:
 * `page` (number, default=0, clamped >=0)
 * `pageSize` (number, default=20, clamped >=0) - Note: not used for now
 * `country[]` (string[]),
 * `state[]` (string[]),
 * `name` (string)
 * `locationSearch` (string)
 */
export const useQueryParams = () => {
	const location = useLocation();
	const navigate = useNavigate();

	// Parse existing query params from URL
	const parsed = useMemo(() => {
		const params = queryString.parse(location.search, {
			arrayFormat: 'comma'
		});

		const toStringArray = (value: unknown): string[] => {
			if (Array.isArray(value)) {
				return value.filter(
					(v): v is string => typeof v === 'string' && v.length > 0
				);
			}
			return typeof value === 'string' && value.length > 0 ? [value] : [];
		};

		const country = toStringArray(params.country);
		const state = toStringArray(params.state);
		const specialtyGroup = toStringArray(params.specialtyGroup);
		const specialty = toStringArray(params.specialty);

		const name = parseNameParam(params.name);
		const page = parseNumberOrDefault(params.page, 1, 1);
		const locationSearch =
			typeof params.locationSearch === 'string' ? params.locationSearch : '';
		// Note(Filip): for now, we do not use pageSize; it is capped on the backend and the client will always use `DEFAULT_PAGE_SIZE = 20` for now.
		// const pageSize = parseNumberOrDefault(params.pageSize, DEFAULT_PAGE_SIZE, 0);

		const specialtySearch =
			typeof params.specialtySearch === 'string' ? params.specialtySearch : '';

		const hcpType = toStringArray(params.hcpType);
		const hcoType = toStringArray(params.hcoType);
		const hco = toStringArray(params.hco);

		const includeInactive = params.includeInactive === 'true' || undefined;

		const dcrSearch =
			typeof params.dcrSearch === 'string' ? params.dcrSearch : '';
		const dcrStatus = toStringArray(params.dcrStatus);

		return {
			page,
			// pageSize ,
			name,
			country,
			state,
			locationSearch,
			specialtySearch,
			specialtyGroup,
			specialty,
			hcpType,
			hcoType,
			hco,
			includeInactive,
			dcrSearch,
			dcrStatus
		} as QueryParams;
	}, [location.search]);

	// Helper to update query params
	const setQueryParams = useCallback(
		(updatedValues: Partial<QueryParams>, options?: { replace?: boolean }) => {
			// Merge existing params with any updated values
			const merged = {
				...parsed,
				...updatedValues
			};

			navigate(
				{
					pathname: location.pathname,
					search: generateQueryString(merged)
				},
				{ replace: options?.replace }
			);
		},
		[parsed, navigate, location.pathname]
	);

	return {
		...parsed,
		setQueryParams
	};
};
